import { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck, SignOut, Bell, CheckCircle, Pencil, MagnifyingGlass, Trash, Warning, Clock, CurrencyDollar, Key, Eye, EyeSlash, BookOpen, Plus, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { adminApi, adminExtraApi, educationApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { PLAN_LIMITS, PLAN_OPTIONS } from "@/lib/plans";

const STATUS_OPTIONS = ["Pendiente", "Activa", "Suspendida", "Bloqueada"];

const STATUS_BADGE = {
  "Pendiente": "border-warning/40 text-warning bg-warning/5",
  "Activa": "border-success/40 text-success bg-success/5",
  "Suspendida": "border-destructive/40 text-destructive bg-destructive/5",
  "Bloqueada": "border-destructive/60 text-destructive bg-destructive/10",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-EC", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [notifs, setNotifs] = useState({ pending: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [revealTarget, setRevealTarget] = useState(null);
  const [revealPwd, setRevealPwd] = useState("");
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealResult, setRevealResult] = useState(null);
  const [revealShowPwd, setRevealShowPwd] = useState(false);
  const [tab, setTab] = useState("usuarios");
  const [articles, setArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleDraft, setArticleDraft] = useState({ title: "", summary: "", body: "", tags: "", order: 100, published: true });
  const [articleSaving, setArticleSaving] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, n] = await Promise.all([adminApi.listUsers(), adminApi.notifications()]);
      setUsers(u);
      setNotifs(n);
    } catch {
      toast.error("No se pudieron cargar los datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!query) return users;
    const q = query.toLowerCase();
    return users.filter((u) =>
      [u.email, u.nombre_empresa, u.nombre_responsable, u.telefono]
        .some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [users, query]);

  const openEdit = (user) => {
    setEditing(user);
    setDraft({
      status: user.status,
      plan: user.plan,
      limite_deudores: user.limite_deudores,
      notas_internas: user.notas_internas || "",
      ultimo_pago_manual: user.ultimo_pago_manual || "",
      ultimo_pago_monto: user.ultimo_pago_monto || "",
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        status: draft.status,
        plan: draft.plan,
        limite_deudores: parseInt(draft.limite_deudores) || 0,
        notas_internas: draft.notas_internas,
        ultimo_pago_manual: draft.ultimo_pago_manual || "",
        ultimo_pago_monto: draft.ultimo_pago_monto === "" ? 0 : parseFloat(draft.ultimo_pago_monto) || 0,
      };
      if (draft.trial_dias !== undefined) {
        payload.trial_dias = parseInt(draft.trial_dias);
      }
      await adminApi.updateUser(editing.id, payload);
      toast.success("Usuario actualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const quickActivate = async (user) => {
    try {
      await adminApi.updateUser(user.id, { status: "Activa" });
      toast.success(`Cuenta de ${user.nombre_empresa} activada`);
      load();
    } catch {
      toast.error("No se pudo activar");
    }
  };

  const quickTrial = async (user, dias = 15) => {
    try {
      const patch = { trial_dias: dias };
      if (user.status !== "Activa") patch.status = "Activa";
      await adminApi.updateUser(user.id, patch);
      toast.success(`Prueba de ${dias} días activada para ${user.nombre_empresa}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo activar la prueba");
    }
  };

  const openDelete = (user) => {
    setDeleting(user);
    setConfirmText("");
  };

  const openReveal = (user) => {
    setRevealTarget(user);
    setRevealPwd("");
    setRevealResult(null);
    setRevealShowPwd(false);
  };

  const submitReveal = async () => {
    if (!revealPwd) return toast.warning("Ingresa tu contraseña de super-admin");
    setRevealBusy(true);
    try {
      const r = await adminExtraApi.revealPassword(revealTarget.id, revealPwd);
      setRevealResult(r);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo revelar la contraseña");
    } finally {
      setRevealBusy(false);
    }
  };

  // Educación
  const loadArticles = useCallback(async () => {
    try {
      const list = await educationApi.adminList();
      setArticles(list);
    } catch {
      toast.error("No se pudieron cargar los artículos");
    }
  }, []);

  useEffect(() => {
    if (tab === "educacion") loadArticles();
  }, [tab, loadArticles]);

  const openArticle = (a) => {
    setEditingArticle(a || { __new: true });
    setArticleDraft({
      title: a?.title || "",
      summary: a?.summary || "",
      body: a?.body || "",
      tags: (a?.tags || []).join(", "),
      order: a?.order ?? 100,
      published: a?.published ?? true,
    });
  };

  const saveArticle = async () => {
    if (!articleDraft.title.trim() || !articleDraft.body.trim()) {
      return toast.warning("Título y contenido son obligatorios");
    }
    setArticleSaving(true);
    try {
      const payload = {
        title: articleDraft.title.trim(),
        summary: articleDraft.summary.trim(),
        body: articleDraft.body,
        tags: articleDraft.tags.split(",").map((t) => t.trim()).filter(Boolean),
        order: parseInt(articleDraft.order) || 100,
        published: !!articleDraft.published,
      };
      if (editingArticle?.__new) {
        await educationApi.adminCreate(payload);
        toast.success("Guía creada");
      } else {
        await educationApi.adminUpdate(editingArticle.id, payload);
        toast.success("Guía actualizada");
      }
      setEditingArticle(null);
      loadArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally {
      setArticleSaving(false);
    }
  };

  const removeArticle = async (a) => {
    setArticleToDelete(a);
  };

  const confirmRemoveArticle = async () => {
    if (!articleToDelete) return;
    try {
      await educationApi.adminDelete(articleToDelete.id);
      toast.success("Guía eliminada");
      setArticleToDelete(null);
      loadArticles();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      const r = await adminApi.deleteUser(deleting.id);
      toast.success(
        `Cuenta eliminada: ${r.deleted_user} (${r.deleted_debtors} deudores, ${r.deleted_receipts} comprobantes)`,
        { duration: 7000 }
      );
      setDeleting(null);
      setConfirmText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar la cuenta");
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    const byStatus = users.reduce((acc, u) => ({ ...acc, [u.status]: (acc[u.status] || 0) + 1 }), {});
    return {
      total: users.length,
      pendientes: byStatus["Pendiente"] || 0,
      activas: byStatus["Activa"] || 0,
      suspendidas: (byStatus["Suspendida"] || 0) + (byStatus["Bloqueada"] || 0),
    };
  }, [users]);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <ShieldCheck size={20} weight="duotone" />
            </div>
            <div>
              <div className="overline">Recobro · Admin</div>
              <div className="font-heading text-lg font-semibold leading-tight">Panel maestro</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowNotifs(true)} data-testid="open-notifs-btn" className="relative">
              <Bell size={16} weight="duotone" className="mr-1.5" />
              Notificaciones
              {notifs.pending > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold">
                  {notifs.pending}
                </span>
              )}
            </Button>
            <span className="text-sm text-muted-foreground hidden md:inline">{admin?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-btn">
              <SignOut size={16} className="mr-1.5" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Cuentas totales" value={stats.total} testid="stat-total" />
          <StatCard label="Pendientes" value={stats.pendientes} tone="warning" testid="stat-pendientes" />
          <StatCard label="Activas" value={stats.activas} tone="success" testid="stat-activas" />
          <StatCard label="Suspendidas / Bloqueadas" value={stats.suspendidas} tone="destructive" testid="stat-suspendidas" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 max-w-md" data-testid="admin-tabs">
            <TabsTrigger value="usuarios" data-testid="tab-usuarios">
              <ShieldCheck size={14} weight="duotone" className="mr-1.5" /> Usuarios
            </TabsTrigger>
            <TabsTrigger value="educacion" data-testid="tab-educacion-admin">
              <BookOpen size={14} weight="duotone" className="mr-1.5" /> Educación
            </TabsTrigger>
          </TabsList>

          <TabsContent value="usuarios" className="mt-4 space-y-4">
        {/* Search */}
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por negocio, email, responsable…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="admin-search"
          />
        </div>

        {/* Users table */}
        <div className="bg-card border border-border rounded-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Cargando usuarios…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground" data-testid="admin-empty">
              No hay usuarios que coincidan.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Negocio</TableHead>
                    <TableHead>Email · Responsable</TableHead>
                    <TableHead>Registro</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Deudores</TableHead>
                    <TableHead>Prueba</TableHead>
                    <TableHead>Último pago</TableHead>
                    <TableHead>Último acceso</TableHead>
                    <TableHead>Aceptó T&C</TableHead>
                    <TableHead className="text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <div className="font-medium">{u.nombre_empresa}</div>
                        <div className="text-xs text-muted-foreground">{u.telefono} {u.ruc && `· ${u.ruc}`}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{u.email}</div>
                        <div className="text-xs text-muted-foreground">{u.nombre_responsable || "—"}</div>
                      </TableCell>
                      <TableCell className="text-xs">{fmtDate(u.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-full ${STATUS_BADGE[u.status] || ""}`}>
                          {u.status}
                        </Badge>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="rounded-full">{u.plan}</Badge></TableCell>
                      <TableCell className="font-mono text-sm">
                        {u.deudores_actuales} / {u.limite_deudores}
                      </TableCell>
                      <TableCell className="text-xs">
                        {u.trial_until ? (
                          <span
                            className="inline-flex items-center gap-1 text-primary"
                            data-testid={`trial-active-${u.id}`}
                            title={`Prueba activa hasta ${u.trial_until}`}
                          >
                            <Clock size={12} weight="duotone" /> hasta {u.trial_until}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {u.ultimo_pago_manual ? (
                          <div className="space-y-0.5">
                            <div className="font-medium text-foreground">{fmtDate(u.ultimo_pago_manual)}</div>
                            {u.ultimo_pago_monto > 0 && (
                              <div className="font-mono text-primary">${u.ultimo_pago_monto.toFixed(2)}</div>
                            )}
                          </div>
                        ) : u.ultimo_pago ? (
                          <span className="text-muted-foreground">{fmtDate(u.ultimo_pago)}</span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{fmtDate(u.last_login_at)}</TableCell>
                      <TableCell className="text-xs">
                        {u.accepted_terms_at ? (
                          <span className="inline-flex items-center gap-1 text-success">
                            <CheckCircle size={12} weight="bold" /> {fmtDate(u.accepted_terms_at)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end flex-wrap">
                          {u.status === "Pendiente" && (
                            <Button size="sm" onClick={() => quickActivate(u)} data-testid={`activate-${u.id}`}>
                              Activar
                            </Button>
                          )}
                          {u.role !== "admin" && !u.trial_until && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => quickTrial(u, 15)}
                              data-testid={`trial-15-${u.id}`}
                              className="border-primary/40 text-primary hover:bg-primary/10 hover:text-primary"
                              title="Activar prueba gratuita de 15 días"
                            >
                              <Clock size={14} weight="duotone" className="mr-1" /> Prueba 15 días
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-${u.id}`}>
                            <Pencil size={14} className="mr-1" /> Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReveal(u)}
                            data-testid={`reveal-pwd-${u.id}`}
                            className="border-warning/40 text-warning hover:bg-warning/10 hover:text-warning"
                            title="Ver contraseña (requiere super-admin)"
                          >
                            <Key size={14} weight="duotone" className="mr-1" /> Contraseña
                          </Button>
                          {u.role !== "admin" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDelete(u)}
                              data-testid={`delete-${u.id}`}
                              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              title="Eliminar cuenta y todos sus datos"
                            >
                              <Trash size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="educacion" className="mt-4 space-y-4" data-testid="admin-education-section">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-semibold tracking-tight">Educación Financiera</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Gestiona las guías que ven todos los usuarios. Los cambios aquí se reflejan inmediatamente en la biblioteca.
                </p>
              </div>
              <Button onClick={() => openArticle(null)} data-testid="new-article-btn">
                <Plus size={14} weight="bold" className="mr-1.5" /> Nueva guía
              </Button>
            </div>

            <div className="bg-card border border-border rounded-md">
              {articles.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Cargando o sin guías.</div>
              ) : (
                <div className="divide-y divide-border">
                  {articles.map((a) => (
                    <div key={a.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`admin-article-${a.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-muted-foreground w-8">#{a.order}</span>
                          <div className="font-medium truncate">{a.title}</div>
                          {!a.published && (
                            <Badge variant="outline" className="rounded-full text-[10px]">Borrador</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate pl-10">{a.summary}</div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => openArticle(a)} data-testid={`edit-article-${a.id}`}>
                          <Pencil size={14} className="mr-1" /> Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeArticle(a)}
                          data-testid={`delete-article-${a.id}`}
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar cuenta — {editing?.nombre_empresa}</DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Estado</Label>
                <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v })}>
                  <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Plan</Label>
                <Select
                  value={draft.plan}
                  onValueChange={(v) => setDraft({ ...draft, plan: v, limite_deudores: PLAN_LIMITS[v] })}
                >
                  <SelectTrigger data-testid="edit-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLAN_OPTIONS.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p} · {PLAN_LIMITS[p]} deudores
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Límite de deudores</Label>
                {draft.plan && parseInt(draft.limite_deudores) !== PLAN_LIMITS[draft.plan] && (
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, limite_deudores: PLAN_LIMITS[draft.plan] })}
                    data-testid="reset-limit-btn"
                    className="text-[11px] text-primary hover:underline font-medium"
                  >
                    Restaurar del plan ({PLAN_LIMITS[draft.plan]})
                  </button>
                )}
              </div>
              <Input
                type="number"
                min="0"
                value={draft.limite_deudores}
                onChange={(e) => setDraft({ ...draft, limite_deudores: e.target.value })}
                data-testid="edit-limit"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Auto-ajustado al cambiar de plan. Puedes sobrescribirlo manualmente si este cliente requiere un cupo distinto.
              </p>
            </div>

            <div>
              <Label>Notas internas (privadas)</Label>
              <Textarea
                value={draft.notas_internas}
                onChange={(e) => setDraft({ ...draft, notas_internas: e.target.value })}
                placeholder="Observaciones del cliente, contactos, acuerdos puntuales…"
                rows={3}
                data-testid="edit-notes"
              />
            </div>

            <div className="rounded-md border border-border bg-accent/20 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <CurrencyDollar size={14} weight="duotone" className="text-primary" />
                <Label className="text-sm font-semibold">Registrar último pago del cliente</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Cuando el cliente realice el pago de su suscripción, registra la fecha y el monto aquí.
                Aparecerá en la columna &ldquo;Último pago&rdquo; del listado.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="last-pay-date" className="text-xs">Fecha del pago</Label>
                  <Input
                    id="last-pay-date"
                    type="date"
                    value={draft.ultimo_pago_manual || ""}
                    onChange={(e) => setDraft({ ...draft, ultimo_pago_manual: e.target.value })}
                    data-testid="edit-ultimo-pago-fecha"
                  />
                </div>
                <div>
                  <Label htmlFor="last-pay-amt" className="text-xs">Monto (USD)</Label>
                  <Input
                    id="last-pay-amt"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={draft.ultimo_pago_monto ?? ""}
                    onChange={(e) => setDraft({ ...draft, ultimo_pago_monto: e.target.value })}
                    data-testid="edit-ultimo-pago-monto"
                  />
                </div>
              </div>
              {editing?.ultimo_pago_manual && (
                <div className="flex items-center justify-between text-xs pt-1">
                  <span className="text-muted-foreground">
                    Registrado actualmente: <span className="font-mono font-semibold text-foreground">{editing.ultimo_pago_manual}</span>
                    {editing.ultimo_pago_monto ? ` · $${editing.ultimo_pago_monto.toFixed(2)}` : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, ultimo_pago_manual: "", ultimo_pago_monto: 0 })}
                    data-testid="clear-ultimo-pago"
                    className="text-destructive hover:underline font-medium"
                  >
                    Limpiar
                  </button>
                </div>
              )}
            </div>

            <div className="rounded-md border border-border bg-accent/20 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Clock size={14} weight="duotone" className="text-primary" />
                <Label className="text-sm font-semibold">Prueba gratuita</Label>
              </div>
              {editing?.trial_until ? (
                <p className="text-xs text-muted-foreground">
                  Activa hasta <span className="font-mono font-medium text-foreground">{editing.trial_until}</span>.
                  Puedes extenderla o cancelarla.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Otorga acceso completo por un número de días. Después continuará en su plan regular.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setDraft({ ...draft, trial_dias: 7 })}
                  data-testid="trial-preset-7"
                  className={draft.trial_dias === 7 ? "border-primary text-primary" : ""}
                >
                  7 días
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setDraft({ ...draft, trial_dias: 15 })}
                  data-testid="trial-preset-15"
                  className={draft.trial_dias === 15 ? "border-primary text-primary" : ""}
                >
                  15 días
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setDraft({ ...draft, trial_dias: 30 })}
                  data-testid="trial-preset-30"
                  className={draft.trial_dias === 30 ? "border-primary text-primary" : ""}
                >
                  30 días
                </Button>
                {editing?.trial_until && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setDraft({ ...draft, trial_dias: 0 })}
                    data-testid="trial-cancel"
                    className={`text-destructive hover:bg-destructive/10 hover:text-destructive ${draft.trial_dias === 0 ? "bg-destructive/10" : ""}`}
                  >
                    Cancelar prueba
                  </Button>
                )}
              </div>
              {draft.trial_dias !== undefined && (
                <p className="text-[11px] text-primary" data-testid="trial-draft-hint">
                  {draft.trial_dias > 0
                    ? `Al guardar se activará la prueba por ${draft.trial_dias} días.`
                    : "Al guardar se cancelará la prueba gratuita."}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-save">
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications drawer */}
      <Dialog open={showNotifs} onOpenChange={setShowNotifs}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="notifs-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Bell size={20} weight="duotone" /> Notificaciones internas
            </DialogTitle>
            <DialogDescription>
              Registros pendientes de aprobación y eventos del sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {notifs.items.length === 0 ? (
              <p className="text-muted-foreground text-sm">No hay notificaciones.</p>
            ) : (
              notifs.items.map((n) => (
                <div
                  key={n.id}
                  className={`p-3 rounded-md border ${n.read ? "border-border bg-card" : "border-primary/30 bg-primary/5"}`}
                  data-testid={`notif-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{n.subject}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {n.user_email} · {n.telefono || "sin tel."} · {fmtDate(n.created_at)}
                      </div>
                      {n.nombre_responsable && (
                        <div className="text-xs text-muted-foreground">
                          Responsable: {n.nombre_responsable}
                        </div>
                      )}
                    </div>
                    {!n.read && (
                      <Badge variant="outline" className="rounded-full border-primary/40 text-primary shrink-0">
                        Nuevo
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Delete-user confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => { if (!o) { setDeleting(null); setConfirmText(""); } }}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-heading text-destructive">
              <Warning size={22} weight="duotone" /> Eliminar cuenta
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Estás a punto de eliminar permanentemente la cuenta{" "}
                <span className="font-semibold text-foreground">{deleting?.nombre_empresa}</span>
                {" "}({deleting?.email}).
              </span>
              <span className="block p-3 rounded-md border border-destructive/40 bg-destructive/5 text-sm leading-relaxed">
                Se borrarán también: <span className="font-mono font-medium text-foreground">{deleting?.deudores_actuales}</span> deudores,
                todos sus comprobantes, su configuración bancaria y notificaciones.
                <span className="block mt-1 text-destructive font-medium">Esta acción NO se puede deshacer.</span>
              </span>
              <span className="block pt-1">
                Escribe <span className="font-mono font-semibold text-foreground">ELIMINAR</span> para confirmar:
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="ELIMINAR"
            data-testid="delete-confirm-input"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={saving || confirmText !== "ELIMINAR"}
              data-testid="delete-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {saving ? "Eliminando…" : "Sí, eliminar todo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reveal Password Dialog */}
      <Dialog open={!!revealTarget} onOpenChange={(o) => !o && setRevealTarget(null)}>
        <DialogContent className="max-w-md" data-testid="reveal-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Key size={20} weight="duotone" className="text-warning" />
              Ver contraseña — {revealTarget?.nombre_empresa}
            </DialogTitle>
            <DialogDescription>
              Por seguridad, confirma tu contraseña de super-admin. Las contraseñas se guardan cifradas y sólo se descifran para esta consulta.
            </DialogDescription>
          </DialogHeader>

          {!revealResult ? (
            <div className="space-y-3">
              <div>
                <Label htmlFor="rp-input">Tu contraseña de super-admin</Label>
                <div className="relative">
                  <Input
                    id="rp-input"
                    type={revealShowPwd ? "text" : "password"}
                    value={revealPwd}
                    onChange={(e) => setRevealPwd(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !revealBusy && submitReveal()}
                    autoFocus
                    data-testid="reveal-superadmin-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setRevealShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
                  >
                    {revealShowPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" data-testid="reveal-cancel" onClick={() => setRevealTarget(null)}>Cancelar</Button>
                <Button
                  onClick={submitReveal}
                  disabled={revealBusy || !revealPwd}
                  data-testid="reveal-submit"
                  className="bg-warning text-warning-foreground hover:bg-warning/90"
                >
                  {revealBusy ? "Descifrando…" : "Ver contraseña"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border border-warning/40 bg-warning/5 p-4 space-y-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Contraseña de {revealResult.email}</div>
                {revealResult.available ? (
                  <>
                    <div
                      className="font-mono text-lg font-semibold break-all select-all"
                      data-testid="revealed-password-value"
                    >
                      {revealResult.password}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(revealResult.password);
                        toast.success("Contraseña copiada");
                      }}
                      data-testid="reveal-copy-btn"
                    >
                      <Copy size={14} className="mr-1.5" /> Copiar al portapapeles
                    </Button>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground" data-testid="reveal-unavailable">
                    {revealResult.hint || "No disponible."}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => setRevealTarget(null)} data-testid="reveal-close">Cerrar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Article Delete Confirmation */}
      <AlertDialog open={!!articleToDelete} onOpenChange={(o) => !o && setArticleToDelete(null)}>
        <AlertDialogContent data-testid="article-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-heading">
              <Warning size={22} weight="duotone" className="text-destructive" />
              Eliminar guía
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Confirmas eliminar la guía <span className="font-semibold text-foreground">{articleToDelete?.title}</span>?
              Los usuarios dejarán de verla al instante. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="article-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveArticle}
              data-testid="article-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Article Editor Dialog */}
      <Dialog open={!!editingArticle} onOpenChange={(o) => !o && setEditingArticle(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="article-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingArticle?.__new ? "Nueva guía" : `Editar — ${editingArticle?.title}`}
            </DialogTitle>
            <DialogDescription>
              Los cambios se publican inmediatamente en la biblioteca de todos los usuarios.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="a-title">Título <span className="text-destructive">*</span></Label>
              <Input
                id="a-title"
                value={articleDraft.title}
                onChange={(e) => setArticleDraft({ ...articleDraft, title: e.target.value })}
                data-testid="article-title"
              />
            </div>
            <div>
              <Label htmlFor="a-summary">Resumen (una línea)</Label>
              <Input
                id="a-summary"
                value={articleDraft.summary}
                onChange={(e) => setArticleDraft({ ...articleDraft, summary: e.target.value })}
                data-testid="article-summary"
              />
            </div>
            <div>
              <Label htmlFor="a-body">Contenido <span className="text-destructive">*</span></Label>
              <Textarea
                id="a-body"
                rows={12}
                value={articleDraft.body}
                onChange={(e) => setArticleDraft({ ...articleDraft, body: e.target.value })}
                placeholder="Usa ## para subtítulos y **texto** para negrita."
                data-testid="article-body"
                className="font-mono text-sm"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Formato: <code>## Subtítulo</code> · <code>**negrita**</code> · líneas vacías = párrafo nuevo.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="a-tags">Tags (separados por coma)</Label>
                <Input
                  id="a-tags"
                  value={articleDraft.tags}
                  onChange={(e) => setArticleDraft({ ...articleDraft, tags: e.target.value })}
                  placeholder="convenios, cobros"
                  data-testid="article-tags"
                />
              </div>
              <div>
                <Label htmlFor="a-order">Orden</Label>
                <Input
                  id="a-order"
                  type="number"
                  min="0"
                  value={articleDraft.order}
                  onChange={(e) => setArticleDraft({ ...articleDraft, order: e.target.value })}
                  data-testid="article-order"
                />
              </div>
              <div className="flex items-end gap-2 pb-2">
                <input
                  id="a-published"
                  type="checkbox"
                  checked={articleDraft.published}
                  onChange={(e) => setArticleDraft({ ...articleDraft, published: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                  data-testid="article-published"
                />
                <Label htmlFor="a-published" className="text-sm cursor-pointer">Publicada (visible para usuarios)</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingArticle(null)}>Cancelar</Button>
            <Button onClick={saveArticle} disabled={articleSaving} data-testid="article-save">
              {articleSaving ? "Guardando…" : "Guardar guía"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneCls = {
    success: "text-success",
    warning: "text-warning",
    destructive: "text-destructive",
  }[tone] || "text-foreground";
  return (
    <div className="bg-card border border-border rounded-md p-4" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`font-heading text-3xl font-semibold tracking-tight mt-1 ${toneCls}`}>
        {value}
      </div>
    </div>
  );
}
